// Shared Trivia Item Component
// Displays trivia questions in both main page and admin contexts
// Always shows question UI with metadata in 4-row layout
// Handles all question types (multiple-choice, true-false, multi-select, image-choice, ordered-list)
// In admin mode, question UI is non-functional (display-only)
// Follows the Section, Component & Manager System architecture

import React from 'react';
import { MultipleChoiceQuestion, TrueFalseQuestion, MultiSelectQuestion, ImageChoiceQuestion, OrderedListQuestion } from '../subpages/trivia/components';
import { TriviaLikeButton } from './TriviaLikeButton';

export function TriviaItem({
  trivia,
  selectedAnswer,
  selectedAnswers,
  showResults,
  onAnswerSelect,
  onSubmitAnswer,
  onEdit,
  onDelete,
  manager,
  disabled = false
}) {
  // Determine if this is admin context (has edit/delete handlers)
  const isAdmin = !!(onEdit && onDelete);

  // For ordered-list questions, use selectedAnswers as orderedItems
  // For other question types, use selectedAnswer
  const getOrderedItems = () => {
    if (trivia.questionType === 'ordered-list') {
      return isAdmin ? [] : (selectedAnswers || []);
    }
    return [];
  };

  const getSelectedAnswer = () => {
    if (trivia.questionType === 'ordered-list') {
      return undefined; // ordered-list doesn't use selectedAnswer
    }
    return isAdmin ? undefined : selectedAnswer;
  };

  const getSelectedAnswers = () => {
    if (trivia.questionType === 'multi-select') {
      return isAdmin ? [] : (selectedAnswers || []);
    }
    return [];
  };

  // Render the appropriate question component based on type
  const renderQuestionComponent = () => {
    // In admin mode, question components are completely disabled and non-functional
    const commonProps = {
      trivia,
      showResults: isAdmin ? false : showResults, // Never show results in admin mode
      disabled: isAdmin ? true : disabled // Always disable in admin mode
    };

    switch (trivia.questionType) {
      case 'multiple-choice':
        return (
          <MultipleChoiceQuestion
            {...commonProps}
            selectedAnswer={getSelectedAnswer()}
            onAnswerSelect={isAdmin ? () => {} : onAnswerSelect}
            onSubmitAnswer={isAdmin ? () => {} : onSubmitAnswer}
          />
        );

      case 'true-false':
        return (
          <TrueFalseQuestion
            {...commonProps}
            selectedAnswer={getSelectedAnswer()}
            onAnswerSelect={isAdmin ? () => {} : onAnswerSelect}
            onSubmitAnswer={isAdmin ? () => {} : onSubmitAnswer}
          />
        );

      case 'multi-select':
        return (
          <MultiSelectQuestion
            {...commonProps}
            selectedAnswers={getSelectedAnswers()}
            onAnswerSelect={isAdmin ? () => {} : onAnswerSelect}
            onSubmitAnswer={isAdmin ? () => {} : onSubmitAnswer}
          />
        );

      case 'image-choice':
        return (
          <ImageChoiceQuestion
            {...commonProps}
            selectedAnswer={getSelectedAnswer()}
            onAnswerSelect={isAdmin ? () => {} : onAnswerSelect}
            onSubmitAnswer={isAdmin ? () => {} : onSubmitAnswer}
          />
        );

      case 'ordered-list':
        return (
          <OrderedListQuestion
            {...commonProps}
            orderedItems={getOrderedItems()}
            onOrderChange={isAdmin ? () => {} : onAnswerSelect}
            onSubmitAnswer={isAdmin ? () => {} : onSubmitAnswer}
          />
        );

      default:
        return (
          <div className="learn-trivia-placeholder">
            <p>Question type '{trivia.questionType}' is not yet implemented.</p>
            <small>Coming soon in future phases!</small>
          </div>
        );
    }
  };

  return (
    <div className={`learn-trivia-item ${isAdmin ? 'admin-trivia-item' : ''}`}>
      {/* Row 1: Question + Difficulty */}
      <div className="trivia-row trivia-header-row">
        <h3 className="trivia-question">
          {trivia.question}
          {isAdmin && (
            <span className={`trivia-difficulty trivia-difficulty-${trivia.difficulty} trivia-difficulty-inline`}>
              {trivia.difficulty}
            </span>
          )}
        </h3>
        {!isAdmin && (
          <span className={`trivia-difficulty trivia-difficulty-${trivia.difficulty}`}>
            {trivia.difficulty}
          </span>
        )}
      </div>

      {/* Row 2: Stats (Attempts, Correct Attempts) + Like Button */}
      <div className="trivia-row trivia-stats-row">
        <div className="trivia-stats-left">
          <span className="trivia-stat">
            <i className="fa-solid fa-gamepad"></i> {trivia.attempts || 0}
          </span>
          <span className="trivia-stat">
            <i className="fa-solid fa-check"></i> {trivia.correctAttempts || 0}
          </span>
        </div>
        {!isAdmin && (
          <TriviaLikeButton
            triviaId={trivia.id}
            initialLikes={trivia.likes || 0}
            initialLiked={trivia.userLiked || false}
            manager={manager}
          />
        )}
      </div>

      {/* Row 3: Tags */}
      <div className="trivia-row trivia-tags-row">
        {trivia.tags && trivia.tags.length > 0 ? (
          trivia.tags.map((tag, index) => (
            <span key={index} className="trivia-tag">{tag}</span>
          ))
        ) : (
          <span className="trivia-no-tags">No tags</span>
        )}
      </div>

      {/* Row 4: Question UI (non-functional in admin mode) */}
      <div className="trivia-row trivia-question-row">
        {renderQuestionComponent()}
      </div>

      {/* Admin controls (only shown when handlers provided) */}
      {isAdmin && (
        <div className="trivia-admin-controls">
          <button
            className="admin-action-btn admin-edit-btn"
            onClick={() => onEdit(trivia)}
            title="Edit trivia question"
          >
            <i className="fa-solid fa-edit"></i>
          </button>
          <button
            className="admin-action-btn admin-delete-btn"
            onClick={() => onDelete(trivia)}
            title="Delete trivia question"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      )}
    </div>
  );
}